import { useNavigate } from 'react-router-dom';
import { MrrOverview } from '../../../components/MrrOverview';
import { TileSkeleton } from './TileSkeleton';
import { StatTile } from '../../../components/ui/Card';
import { ErrorState } from '../../../components/ui/States';
import { useSubscriptionsSummary, useInvoicesSummary } from '../../../hooks/useFinance';
import { useCustomers } from '../../../hooks/useCustomers';
import { useMrrOverview } from '../../../hooks/useDashboard';
import { useAccess } from '../../../hooks/useAccess';
import { formatShortDate, money, signedMoney } from './formatters';
import { DataIssues, useFinanceIssues } from './DataIssues';
import { ExpiryTimeline } from './ExpiryTimeline';
import { PlanBreakdownCard } from './PlanBreakdown';

export function OverviewTab() {
  const navigate = useNavigate();
  const { canAccess } = useAccess();
  const subsQuery = useSubscriptionsSummary();
  const customersQuery = useCustomers();
  const mrrQuery = useMrrOverview();
  const invoiceQuery = useInvoicesSummary('all');
  const mrrData = mrrQuery.data;
  const invoiceData = invoiceQuery.data;

  const subs = subsQuery.data?.summary;
  const currency = subs?.currency ?? 'EUR';
  const month = mrrData?.overview.currentMonth;
  const mrrCurrency = mrrData?.overview.currency ?? currency;
  // Net new MRR this month, from the same four movements the MRR card lists.
  const netNew = month ? month.newMrr + month.expansionMrr - month.contractionMrr - month.churnedMrr : null;
  const receivables = invoiceData?.summary;
  // Movements this month that only exist because a reporting override ran out
  // or changed while Odoo stood still (Xerox, ISS North America, Swiss GRC in
  // Oct 26): counted, as on the board sheet, but not new business.
  const overrideMoves = subs?.overrideMovements.filter((n) => n.month === subs.bridge.month && n.kind !== null) ?? [];
  const split = subs?.overrideSplits[subs.bridge.month];
  const overrideNet = split ? split.restoredMrr + split.overrideChangedMrr : 0;
  const nameOf = (customer: string) => subs?.customerNames[customer]?.name ?? customer;
  // Contracts that ended on the 1st but still count for this month's MRR (nextbike):
  // "28 paying" is "27 active + nextbike", the split the Customers tab shows, read
  // from the same list so the two cannot disagree. Absent until that list loads.
  const mrrSummary = customersQuery.data?.summary.mrr;
  const endedNames = mrrSummary && mrrSummary.error === null ? mrrSummary.others.map((o) => o.name) : [];
  const endedRow = mrrSummary && mrrSummary.error === null ? customersQuery.data?.customers.find((c) => c.id === mrrSummary.others[0]?.customerId) : undefined;
  const payingSub = subs
    ? endedNames.length > 0
      ? `This month · ${subs.payingCustomers - endedNames.length} active + ${endedNames.join(', ')}${endedRow?.subscription.expiresOn ? ` (ended ${formatShortDate(endedRow.subscription.expiresOn)})` : ''}`
      : `This month · ${subs.activeSubscriptions} contracts · ARPA ${money(subs.arpa, currency)}`
    : undefined;
  const tilesLoading = subsQuery.isLoading || mrrQuery.isLoading || invoiceQuery.isLoading;

  useFinanceIssues('overview.subscriptions', subs?.issues);
  useFinanceIssues('overview.receivables', receivables?.issues);

  return (
    <div className="space-y-6">
      {subsQuery.error && (
        <ErrorState title="Couldn’t load subscriptions" error={subsQuery.error} onRetry={() => subsQuery.refetch()} />
      )}
      <DataIssues issues={subs?.issues} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tilesLoading ? (
          ['MRR', 'Net new MRR', 'Paying customers', 'Outstanding'].map((label) => <TileSkeleton key={label} label={label} />)
        ) : (
          <>
            <StatTile
              label="MRR"
              value={subs ? money(subs.currentMrr, currency) : 'Unavailable'}
              sub={subs ? `This month, Odoo + MRR sheet · ARR ${money(subs.currentMrr * 12, currency)}` : undefined}
              tooltip="Counted Odoo contracts, renewals counted once, setup fees spread over the term, with the reporting overrides from the MRR sheet applied. Customers and MRR by billing term below add up to this number."
            />
            <StatTile
              label="Net new MRR"
              value={netNew === null ? 'Unavailable' : signedMoney(netNew, mrrCurrency)}
              sub={
                netNew !== null && overrideMoves.length > 0
                  ? `This month · new ${signedMoney(netNew - overrideNet, mrrCurrency)} · sheet rates ${signedMoney(overrideNet, mrrCurrency)}`
                  : 'This month, new + expansion − contraction − churn'
              }
              tooltip={
                overrideMoves.length > 0 && split
                  ? `New + expansion − contraction − churned MRR so far this month. "New" leaves out what sheet rates explain (rate changes in the MRR sheet while the Odoo contracts stood still): rate restored ${signedMoney(split.restoredMrr, mrrCurrency)}, override changed ${signedMoney(split.overrideChangedMrr, mrrCurrency)}. ${overrideMoves
                      .map(
                        (n) =>
                          `${nameOf(n.customer)} ${money(n.from, mrrCurrency)} → ${money(n.to, mrrCurrency)} (${n.kind === 'restored' ? 'override ended' : n.kind === 'changed' ? 'override changed' : `override ${n.kind}`}; Odoo stayed at ${money(n.odooTo, mrrCurrency)}).`,
                      )
                      .join(' ')}`
                  : 'New + expansion − contraction − churned MRR so far this month'
              }
            />
            <StatTile
              label="Paying customers"
              value={subs ? String(subs.payingCustomers) : 'Unavailable'}
              sub={payingSub}
              tooltip={`Customers with MRR counted this month${subs ? `: ${subs.activeSubscriptions} contracts, ARPA ${money(subs.arpa, currency)}` : ''}. A contract that ends on the 1st still counts for its month, so a customer can be here and already ended on the Customers tab.`}
            />
            <StatTile
              label="Outstanding"
              value={receivables ? money(receivables.outstandingTotal, receivables.currency) : 'Unavailable'}
              sub={
                receivables
                  ? `${money(receivables.outstandingTotal - receivables.overdueTotal, receivables.currency)} not yet due · ${money(receivables.overdueTotal, receivables.currency)} overdue${
                      receivables.scheduledTotal > 0
                        ? ` · + ${money(receivables.scheduledTotal, receivables.currency)} scheduled, not owed yet`
                        : ''
                    }`
                  : undefined
              }
              tooltip={
                receivables && receivables.scheduledInvoices.length > 0
                  ? `Open balance on invoices dated today or earlier. ${receivables.scheduledInvoices.length} invoice${
                      receivables.scheduledInvoices.length === 1 ? ' is' : 's are'
                    } dated after today (${receivables.scheduledInvoices
                      .map((i) => `${i.companyName} ${money(i.total, i.currency)} on ${formatShortDate(i.issueDate)}`)
                      .join('; ')}): scheduled, not owed yet, so not counted here.`
                  : 'Open balance on invoices dated today or earlier'
              }
              onClick={canAccess('finance.customers') ? () => navigate('/finance/customers') : undefined}
            />
          </>
        )}
      </div>

      <MrrOverview
        overrideNotes={subs?.overrideMovements}
        overrideSplits={subs?.overrideSplits}
        customerNames={subs?.customerNames}
      />

      {subs && (
        // Stacked, not side by side: the 12-month expiry strip and the plan bars
        // both need the full width to stay readable.
        <div className="space-y-6">
          {subs.contracts.length > 0 && <ExpiryTimeline contracts={subs.contracts} />}
          <PlanBreakdownCard items={subs.byPlan} currency={subs.currency} bridge={subs.bridge} contracts={subs.contracts} />
        </div>
      )}
    </div>
  );
}
